import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
type Size = 'sm' | 'md' | 'lg';

/** Брендбук: основная кнопка — заливка signal, текст on-signal; наведение подчёркивает текст, цвет не меняется */
const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-accent text-on-accent font-semibold hover:underline underline-offset-[3px] disabled:bg-tile disabled:text-ink-2 disabled:no-underline',
  accent:
    'bg-accent text-on-accent font-semibold hover:underline underline-offset-[3px] disabled:bg-tile disabled:text-ink-2 disabled:no-underline',
  secondary:
    'bg-surface text-ink border border-line-strong hover:underline underline-offset-[3px] disabled:bg-tile disabled:border-tile disabled:text-ink-2 disabled:no-underline',
  ghost: 'text-ink-2 hover:text-ink hover:bg-tile/70 disabled:text-ink-3',
  danger: 'bg-surface text-danger border border-danger/60 hover:bg-danger-soft disabled:opacity-50',
};

/** На телефоне кнопки не ниже 44 px — пальцем не промахнуться; на компьютере — компактнее */
const SIZES: Record<Size, string> = {
  sm: 'h-11 md:h-8 px-3 text-[13px] gap-1.5',
  md: 'h-11 md:h-10 px-4 text-[14px] gap-2',
  lg: 'h-12 px-5 text-[15px] gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading,
    icon,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

export function IconButton({
  label,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={clsx(
        'inline-flex size-11 items-center justify-center rounded-md text-ink-2 transition-colors hover:bg-tile/70 hover:text-ink md:size-9',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
